'use client'

import { getI18n, getLocalizedUri, I18nDictionary, LangCode } from '@/components/i18n/lang-context'
import { Section } from '@/components/jbda/section'
import { SectionTitle } from '@/components/jbda/section-title'
import { PartnerCard, showMore } from '@/components/partners/partner-card'
import { PressArticleCard } from '@/components/press/press-article-card'
import { Article } from '@/components/press/press-articles'
import { FlexCol } from '@/components/ui/flex'
import { cn } from '@/lib/utils/utils'
import { Partner } from '@/types/partner'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

const I18N_PRESS: I18nDictionary = {
  title: {
    en: 'They talk about us',
    fr: 'Ils parlent de nous'
  },
  see_more: {
    en: 'View all publications...',
    fr: 'Voir toutes les publications...'
  }
}
const I18N_PARTNERS: I18nDictionary = {
  title: {
    en: 'They trust us',
    fr: 'Ils nous font confiance'
  },
  see_more: {
    en: 'Discover all our partners ...',
    fr: 'Découvrez tous nos partenaires...'
  }
}

export function LBCarrousel({langCode, items, partner, noTitle=false}: {langCode: LangCode, items: Partner[]|Article[], partner: Partner|undefined, noTitle?: boolean}) {
  
  const press = items[0].type !== 'real-estate' && items[0].type !== 'finance'
  if(!!partner) {
    if(press) {
      const articles = items as Article[]
      items = [
        ...articles.filter(article => article.source === partner.id),
        ...articles.filter(article => article.source !== partner.id)
      ]
    } else {
      const partners = items as Partner[]
      items = partners.map((p) => p.id === partner.id ? partner : {...p, description: undefined, location: {} })
    }
  }
  const [partnerOnly, setPartnerOnly] = useState<boolean>()

  const i18n = getI18n(press ? I18N_PRESS : I18N_PARTNERS, langCode)
  const urlSeeMore = getLocalizedUri(press ? '/press' : '/partners', langCode)

  const [leftReached, setLeftReached] = useState<boolean>(true)
  const [rightReached, setRightReached] = useState<boolean>(true)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const scrollAmount = 320

  useEffect(() => {
    setPartnerOnly(!press && !!partner)
    const el = scrollContainerRef.current;
    if (!el) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el
      setLeftReached(scrollLeft <= 12)
      setRightReached(scrollLeft + clientWidth >= scrollWidth - 12)
    };

    handleScroll(); // Vérifie la position initiale
    el.addEventListener("scroll", handleScroll);

    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleScrollFirst = () => {
    if (scrollContainerRef.current && !leftReached) {
      scrollContainerRef.current.scrollLeft = 0
    }
  }
  const handleScrollLeft = () => {
    if (scrollContainerRef.current && !leftReached) {
      scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
    }
  }
  const handleScrollRight = () => {
    if (scrollContainerRef.current && !rightReached) {
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }
  const handleScrollLast = () => {
    if (scrollContainerRef.current && !rightReached) {
      scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth
    }
  }

  const buildItemsCard = () => {
    const _items = !partnerOnly ? items : (items as Partner[]).filter((item: any) => item.id === partner?.id)
    const _others = !partnerOnly ? [] : (items as Partner[]).filter((item: any) => item.id !== partner?.id)
    return (<>{_items.map((item: Partner|Article, index) => 
      <div key={index}
        className={cn('flex ',
          press ? 
            (item as Article).source === partner?.id ? 'sm:min-w-[444px]' : '' :
            (item as Partner).id === partner?.id ? 'sm:min-w-[592px]' : '',
          ' min-w-[296px] snap-start flex-col')}
      >
        {press ?
          <PressArticleCard article={item as Article} langCode={langCode} isLink={false} /> :
          <PartnerCard partner={item as Partner} langCode={langCode} embedded={true} />}
      </div>
    )}{partnerOnly ? (
      <div key={'more partner'} className={cn('flex', noTitle ? '' : 'sm:min-w-[592px]', 'min-w-[296px] snap-start flex-col cursor-pointer')} onClick={()=>setPartnerOnly(false)}>
        {showMore(_others, langCode, true, noTitle)}
      </div>) : <></>
    }</>)
  }

  return (
    <Section contentClassName={noTitle ? 'py-0 sm:py-0' : 'py-12 md:py-20 md:pt-12'}>
      <FlexCol className='w-full items-center gap-8'>
        {!noTitle && <SectionTitle title={i18n.title} langCode={langCode} />}
        <div className='relative w-full'>
          <button
            onClick={handleScrollFirst}
            className='absolute left-1 md:-left-2 top-full -translate-y-16 z-10 rounded-full bg-white/80 p-2 shadow-md transition hover:bg-white focus:outline-none'
            aria-label='Scroll left'
          >
            <div className='relative'>
              <FaChevronLeft className={cn('translate-x-1 h-4 w-4 ', !leftReached ? 'text-primary' : 'text-gray-400')} />
              <FaChevronLeft className={cn('absolute inset-0 m-auto -translate-x-1 h-4 w-4', !leftReached ? 'text-primary' : 'text-gray-400')} />
            </div>
          </button>
          <button
            onClick={handleScrollLeft}
            className='absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/80 p-2 shadow-md transition hover:bg-white focus:outline-none'
            aria-label='Scroll left'
          >
            <FaChevronLeft className={cn('h-8 w-8', !leftReached ? 'text-primary' : 'text-gray-400')} />
          </button>
          <div
            ref={scrollContainerRef}
            className='pretty-scrollbar flex w-full snap-x scroll-pl-1 space-x-4 overflow-x-auto scroll-smooth p-4'
          >
            {buildItemsCard()}
          </div>
          <button
            onClick={handleScrollRight}
            className='absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/90 p-2 shadow-md transition hover:bg-white focus:outline-none'
            aria-label='Scroll right'
          >
            <FaChevronRight className={cn('h-8 w-8', !rightReached ? 'text-primary' : 'text-gray-400')} />
          </button>
          <button
            onClick={handleScrollLast}
            className='absolute right-1 md:-right-2 top-full -translate-y-16 z-10 rounded-full bg-white/80 p-2 shadow-md transition hover:bg-white focus:outline-none'
            aria-label='Scroll right'
          >
            <div className='relative'>
              <FaChevronRight className={cn('translate-x-1 h-4 w-4', !rightReached ? 'text-primary' : 'text-gray-400')} />
              <FaChevronRight className={cn('absolute inset-0 m-auto -translate-x-1 h-4 w-4', !rightReached ? 'text-primary' : 'text-gray-400')} />
            </div>
          </button>
        </div>
        <Link href={urlSeeMore} onClick={() => window.scrollTo(0, 0)} className='-mt-2 text-primary hover:underline'>
          {i18n.see_more}
        </Link>
      </FlexCol>
    </Section>
  )
}
